import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { Plus, Trash2, ShieldAlert, CheckCircle2, Pencil, RotateCcw } from 'lucide-react';
import { Dialog } from '../../components/ui';
import { useConfirm } from '../../components/ConfirmDialog';
import {
  PageHead, FigureStrip, Figure, Register, DetailDrawer, AcStatus, Deadline, Field, FormRow, FilterChips, fmtDateTime,
} from './design';
import { CiActions, ToImprovement, SirsTriage } from './Improvement';

const CATEGORIES = ['Unreasonable use of force', 'Unlawful or inappropriate sexual conduct', 'Psychological or emotional abuse', 'Unexpected death', 'Stealing or financial coercion', 'Neglect', 'Inappropriate use of restrictive practices', 'Unexplained absence (missing)'];
const STATUSES = ['open', 'notified', 'under_investigation', 'not_reportable', 'closed'];
const cap = (s) => String(s || '').replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

// Older and seeded rows store a category without its bracketed note ('Unexplained absence' for
// 'Unexplained absence (missing)'). Match those to the list entry so the drawer shows the category
// the incident really has; anything else unknown is kept as-is and offered as its own option, so
// opening and saving an incident never swaps its category for the first one in the list.
const normCategory = (c) => {
  if (!c) return c;
  const key = (s) => String(s).replace(/\s*\(.*\)\s*$/, '').trim().toLowerCase();
  return CATEGORIES.find((x) => x === c) || CATEGORIES.find((x) => key(x) === key(c)) || c;
};

// incident_date is a bare TIMESTAMP holding Sydney wall-clock. The API sends it as the matching
// instant, so writing that instant out in Sydney time gives back exactly what is stored, whatever
// zone the browser is in. toISOString() wrote UTC, which put the default ~10 hours out and moved
// the time by 10 hours every time an incident was edited and saved.
const SYDNEY_PARTS = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Australia/Sydney', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
});
const sydneyInputValue = (v = new Date()) => {
  const d = v instanceof Date ? v : new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  const p = Object.fromEntries(SYDNEY_PARTS.formatToParts(d).map((x) => [x.type, x.value]));
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
};

// The server computes hours_to_due with EXTRACT(EPOCH ...) precisely because a bare TIMESTAMP comes
// through node-postgres skewed by the server's offset. So the deadline shown here is rebuilt from
// those hours whenever they are present, and only falls back to the raw column when they are not.
const dueAt = (inc) => {
  if (inc.hours_to_due != null) return new Date(Date.now() + Number(inc.hours_to_due) * 3600e3);
  return inc.notification_due ? new Date(inc.notification_due) : null;
};

// Row urgency: rose once the deadline has passed unreported, amber inside the last 24 hours.
const deadlineTone = (inc) => {
  if (inc.notified_at) return undefined;
  const due = dueAt(inc);
  if (!due) return undefined;
  const h = (due - Date.now()) / 36e5;
  if (inc.overdue || h < 0) return 'rose';
  if (h <= 24) return 'amber';
  return undefined;
};

// The deadline is the information. Once the report has been recorded, the cell says when; until
// then it counts down, with the exact time underneath because a priority 1 has hours, not days.
function DeadlineCell({ inc }) {
  if (inc.notified_at) {
    return <span className="text-slate-500 tabular-nums">Recorded as reported {fmtDateTime(inc.notified_at)}</span>;
  }
  const due = dueAt(inc);
  if (!due) return <span className="text-slate-300">—</span>;
  return (
    <span className="block leading-tight">
      <Deadline date={due} absolute={false} />
      <span className="block text-[11px] text-slate-500 tabular-nums mt-0.5">{fmtDateTime(due)}</span>
    </span>
  );
}

function TextBlock({ label, text }) {
  return (
    <div>
      <dt className="text-[12px] font-medium text-slate-500">{label}</dt>
      <dd className="text-[13px] text-slate-800 whitespace-pre-wrap mt-1">{text || <span className="text-slate-500">Nothing recorded.</span>}</dd>
    </div>
  );
}

export default function AgedCareSirs({ clientId: only = '', embedded = false } = {}) {
  const [list, setList] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [modal, setModal] = useState(null); // {editing?}
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  const [viewingId, setViewingId] = useState(null);
  const confirm = useConfirm();

  useEffect(() => { api.get('/aged-care/clients').then((r) => setClients(r.data || [])).catch(() => {}); }, []);
  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try { const r = await api.get('/aged-care/sirs', { params: filter ? { status: filter } : {} }); setList(r.data || []); }
    catch (e) { setList([]); setLoadError(e.response?.data?.message || 'Could not load the incidents'); } finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);
  // Inside a participant's file, only theirs.
  const visible = only ? list.filter((i) => String(i.client_id) === String(only)) : list;

  const openNew = () => { setF({ client_id: only || '', incident_date: sydneyInputValue(), aware_at: '', category: CATEGORIES[0], priority: 2, reportable: true, description: '', immediate_actions: '', reported_by: '' }); setModal({}); };
  const openEdit = (i) => { setF({ ...i, category: normCategory(i.category), incident_date: i.incident_date ? sydneyInputValue(i.incident_date) : '', aware_at: i.aware_at ? sydneyInputValue(i.aware_at) : '' }); setModal({ editing: i }); };
  const submit = async () => {
    if (!f.incident_date || !f.category) return toast.error('Incident date and category are required');
    if (f.aware_at && f.aware_at < f.incident_date) return toast.error('The time you became aware cannot be before the incident happened');
    setSaving(true);
    const body = { client_id: f.client_id || null, incident_date: f.incident_date, aware_at: f.aware_at || null, category: f.category, priority: Number(f.priority), reportable: !!f.reportable, description: f.description, immediate_actions: f.immediate_actions, reported_by: f.reported_by, ...(modal.editing ? { status: f.status, follow_up: f.follow_up } : {}) };
    try {
      if (modal.editing) await api.put(`/aged-care/sirs/${modal.editing.id}`, body);
      else await api.post('/aged-care/sirs', body);
      toast.success('Incident saved'); setModal(null); load();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save the incident'); } finally { setSaving(false); }
  };
  // Records that the incident was reported. It stamps notified_at and nothing else — the report
  // itself is made through the My Aged Care provider portal, not from here. Without a portal
  // reference it asks first: one stray click in the register would stop the deadline counting.
  const notify = async (i, reference) => {
    if (!reference) {
      const ok = await confirm(
        'Only record this once the incident has been reported in the My Aged Care provider portal. Now is stamped as the time it was reported and the deadline stops counting. You can correct the time and add the portal reference afterwards.',
        { title: 'Record this incident as reported?', confirmLabel: 'Record as reported' },
      );
      if (!ok) return;
    }
    try {
      await api.patch(`/aged-care/sirs/${i.id}/notify`, reference ? { reference } : {});
      toast.success(reference ? `Recorded as reported, reference ${reference}` : 'Recorded as reported');
      setPack(null); setPortalRef('');
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not record it as reported. Try again.'); }
  };
  // Recorded as reported by mistake: the stamp comes off and the deadline counts again.
  const undoReported = async (i) => {
    const ok = await confirm(
      'Use this if it was recorded as reported by mistake. It no longer counts as reported, so its deadline counts again. The portal reference is kept.',
      { title: 'Undo recorded as reported?', confirmLabel: 'Undo reported', danger: true },
    );
    if (!ok) return;
    try {
      await api.patch(`/aged-care/sirs/${i.id}/notify`, { undo: true });
      toast.success('No longer recorded as reported');
      setCorrecting(null);
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not undo it'); }
  };
  // Corrects when it was reported and the portal reference without stamping it again, for a
  // report recorded late in the register or a reference typed wrong. Times are Sydney wall-clock,
  // the same as the incident's own.
  const [correcting, setCorrecting] = useState(null); // { id, notifiedAt, reference }
  const [savingCorrection, setSavingCorrection] = useState(false);
  const startCorrect = (i) => setCorrecting({ id: i.id, notifiedAt: i.notified_at ? sydneyInputValue(i.notified_at) : '', reference: i.portal_reference || '' });
  const saveCorrection = async (i) => {
    const c = correcting;
    if (!c || !c.notifiedAt) return toast.error('Give the date and time it was reported');
    if (c.notifiedAt > sydneyInputValue()) return toast.error('The time it was reported cannot be in the future');
    if (i.incident_date && c.notifiedAt < sydneyInputValue(i.incident_date)) return toast.error('It cannot have been reported before the incident happened');
    setSavingCorrection(true);
    try {
      await api.patch(`/aged-care/sirs/${i.id}/notify`, { correct: true, notifiedAt: c.notifiedAt, reference: c.reference.trim() });
      toast.success('Report details corrected');
      setCorrecting(null);
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save the correction'); } finally { setSavingCorrection(false); }
  };
  // The portal report, prepared: every field the My Aged Care SIRS form asks for, ready to copy.
  const [pack, setPack] = useState(null);
  const [portalRef, setPortalRef] = useState('');
  const preparePack = async (i) => {
    try { const r = await api.get(`/aged-care/sirs/${i.id}/notification-pack`); setPack({ id: i.id, ...r.data }); }
    catch { toast.error('Could not prepare the report'); }
  };
  const copy = async (text, what) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${what} copied`); }
    catch { toast.error('Copy did not work in this browser. Select the text and copy it instead.'); }
  };
  const del = async (i) => {
    const ok = await confirm(`The ${i.category ? `"${i.category}" ` : ''}incident is taken off the register as entered in error or a duplicate. The record itself is kept for the retention period.`, { title: 'Remove this incident from the register?', confirmLabel: 'Remove', danger: true });
    if (!ok) return;
    try {
      await api.delete(`/aged-care/sirs/${i.id}`, { data: { reason: 'entered in error or a duplicate' } });
      toast.success('Incident removed from the register. The record is kept.');
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not remove the incident'); }
  };

  const open = list.filter((i) => i.status !== 'closed').length;
  const overdue = list.filter((i) => i.overdue && !i.notified_at).length;
  const p1 = list.filter((i) => i.priority === 1 && i.status !== 'closed').length;
  const reported = list.filter((i) => i.notified_at).length;
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const editing = modal && modal.editing ? modal.editing : null;
  const viewing = viewingId ? list.find((i) => i.id === viewingId) : null;
  const record = editing || viewing || null;
  // Stable, or the drawer and dialog re-run their focus effects on every keystroke in the form and
  // pull focus out of the field being typed in.
  const closeDrawer = useCallback(() => { setModal(null); setViewingId(null); setCorrecting(null); }, []);
  const closeModal = useCallback(() => setModal(null), []);

  const columns = [
    { key: 'priority', header: 'Priority', width: 110, sortable: true, render: (i) => <span className={clsx('badge', i.priority === 1 ? 'badge-red' : 'badge-yellow')}>Priority {i.priority}</span> },
    { key: 'category', header: 'Category', truncate: true, sortable: true, render: (i) => (
      <span className="block min-w-0">
        <span className="block truncate font-medium text-slate-800">{i.category}</span>
        {!i.reportable && <span className="block text-[11px] text-slate-500">Not reportable</span>}
      </span>
    ) },
    { key: 'client_name', header: 'Participant', truncate: true, sortable: true, render: (i) => i.client_name || <span className="text-slate-300">—</span> },
    { key: 'incident_date', header: 'Occurred', width: 165, sortable: true, render: (i) => <span className="tabular-nums text-slate-500 whitespace-nowrap">{fmtDateTime(i.incident_date)}</span> },
    { key: 'notification_due', header: 'Deadline', width: 230, sortable: true, sortValue: (i) => (i.notified_at ? null : (i.hours_to_due == null ? null : Number(i.hours_to_due))), render: (i) => <DeadlineCell inc={i} /> },
    { key: 'status', header: 'Status', width: 150, sortable: true, render: (i) => <AcStatus status={i.status} /> },
  ];

  const form = (
    <div className="space-y-4">
      <FormRow cols={2}>
        <Field label="Participant">
          {(id) => (
            <select id={id} className="input-field" value={f.client_id || ''} onChange={set('client_id')}>
              <option value="">Not linked to a participant</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
        </Field>
        <Field label="Occurred" required>{(id) => <input id={id} type="datetime-local" className="input-field" value={f.incident_date || ''} onChange={set('incident_date')} />}</Field>
      </FormRow>
      <Field label="When you became aware" hint="The 24-hour and 30-day deadlines count from this. Leave it blank if you have just found out, and the time you save it is used.">
        {(id) => <input id={id} type="datetime-local" className="input-field" value={f.aware_at || ''} min={f.incident_date || undefined} onChange={set('aware_at')} />}
      </Field>
      <Field label="Category" required>
        {(id) => (
          <select id={id} className="input-field" value={f.category || ''} onChange={set('category')}>
            {f.category && !CATEGORIES.includes(f.category) && <option value={f.category}>{f.category}</option>}
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
      </Field>
      <Field label="Priority" hint="The reporting deadline is counted from when you became aware of it.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <button type="button" onClick={() => setF((p) => ({ ...p, priority: 1 }))} aria-pressed={Number(f.priority) === 1}
            className={clsx('rounded-lg border px-3 py-2 text-left text-[13px] transition-colors', Number(f.priority) === 1 ? 'border-red-300 bg-red-50 text-red-800' : 'border-slate-200 text-slate-600 hover:bg-slate-50')}>
            <span className="block font-medium">Priority 1</span>
            <span className="block text-[11.5px] opacity-80">Report within 24 hours</span>
          </button>
          <button type="button" onClick={() => setF((p) => ({ ...p, priority: 2 }))} aria-pressed={Number(f.priority) === 2}
            className={clsx('rounded-lg border px-3 py-2 text-left text-[13px] transition-colors', Number(f.priority) === 2 ? 'border-amber-300 bg-amber-50 text-amber-800' : 'border-slate-200 text-slate-600 hover:bg-slate-50')}>
            <span className="block font-medium">Priority 2</span>
            <span className="block text-[11.5px] opacity-80">Report within 30 days</span>
          </button>
        </div>
      </Field>
      <label className="flex items-start gap-2.5 text-[13px] text-slate-700 cursor-pointer">
        <input type="checkbox" className="mt-0.5" checked={!!f.reportable} onChange={(e) => setF((p) => ({ ...p, reportable: e.target.checked }))} />
        <span>
          <span className="font-medium">Reportable to the Aged Care Quality and Safety Commission</span>
          <span className="block text-[11.5px] text-slate-500 mt-0.5">Untick for an incident that is not reportable under SIRS. It stays on the register either way.</span>
        </span>
      </label>
      <Field label="Description">{(id) => <textarea id={id} rows={3} className="input-field" value={f.description || ''} onChange={set('description')} />}</Field>
      <Field label="Immediate actions taken">{(id) => <textarea id={id} rows={2} className="input-field" value={f.immediate_actions || ''} onChange={set('immediate_actions')} />}</Field>
      <FormRow cols={2}>
        <Field label="Reported by">{(id) => <input id={id} className="input-field" value={f.reported_by || ''} onChange={set('reported_by')} />}</Field>
        {editing && (
          <Field label="Status">
            {(id) => <select id={id} className="input-field" value={f.status} onChange={set('status')}>{STATUSES.map((s) => <option key={s} value={s}>{cap(s)}</option>)}</select>}
          </Field>
        )}
      </FormRow>
      {editing && <Field label="Follow-up">{(id) => <textarea id={id} rows={2} className="input-field" value={f.follow_up || ''} onChange={set('follow_up')} />}</Field>}
    </div>
  );

  return (
    <div>
      {!embedded && <>
      <PageHead icon={ShieldAlert}
        title="SIRS incidents"
        purpose="Log serious incidents and track each one's reporting deadline: 24 hours for a priority 1 incident, 30 days for a priority 2, counted from when you became aware of it. Incidents are reported through the My Aged Care provider portal; this register records that it was done."
        actions={<button type="button" onClick={openNew} className="btn-primary"><Plus size={15} /> Log incident</button>}
      />

      <FigureStrip>
        <Figure label="Open incidents" value={open} />
        <Figure label="Past deadline, not yet reported" value={overdue} tone={overdue ? 'red' : 'muted'} />
        <Figure label="Priority 1 still open" value={p1} tone={p1 ? 'amber' : 'muted'} />
        <Figure label="Recorded as reported" value={reported} />
      </FigureStrip>
      </>}

      <Register
        title={embedded ? 'Serious incidents' : 'Incident register'} count={visible.length} countLabel={visible.length === 1 ? 'incident' : 'incidents'}
        actions={embedded ? <button type="button" onClick={openNew} className="btn-secondary btn-sm"><Plus size={14} /> Log incident</button> : undefined}
        chips={(
          <FilterChips
            multi={false} ariaLabel="Filter by status" value={filter || null} onChange={(v) => setFilter(v || '')}
            options={[{ value: '', label: 'All' }, ...STATUSES.map((s) => ({ value: s, label: cap(s) }))]}
          />
        )}
        columns={columns} rows={visible} rowKey="id" loading={loading} error={loadError} onRetry={load}
        onRowClick={(i) => setViewingId(i.id)} activeRowKey={record ? record.id : null} rowTone={deadlineTone}
        rowActions={(i) => (
          <>
            {!i.notified_at && (
              <button type="button" onClick={(e) => { e.stopPropagation(); notify(i); }} className="btn-ghost btn-sm">
                <CheckCircle2 size={14} /> Record as reported
              </button>
            )}
            <button type="button" onClick={(e) => { e.stopPropagation(); openEdit(i); }} className="btn-ghost btn-sm" aria-label="Edit incident"><Pencil size={14} /></button>
            <button type="button" onClick={(e) => { e.stopPropagation(); del(i); }} className="btn-ghost btn-sm hover:text-red-700" aria-label="Delete incident"><Trash2 size={14} /></button>
          </>
        )}
        empty={{
          icon: ShieldAlert,
          title: filter ? `No ${cap(filter).toLowerCase()} incidents` : 'No incidents logged',
          hint: filter ? 'Clear the status filter to see the whole register.' : 'Log an incident as soon as you become aware of it. The reporting deadline is counted from when you became aware.',
          action: filter ? undefined : <button type="button" onClick={openNew} className="btn-primary"><Plus size={15} /> Log incident</button>,
        }}
      />

      {record && (
        <DetailDrawer
          open onClose={closeDrawer} icon={ShieldAlert} width="md"
          title={editing ? 'Edit incident' : record.category}
          subtitle={editing ? record.category : (record.client_name || 'Not linked to a participant')}
          statusNode={<AcStatus status={record.status} />}
          meta={editing ? undefined : [
            { label: 'Priority', value: `Priority ${record.priority}` },
            { label: 'Occurred', value: fmtDateTime(record.incident_date) },
            { label: 'Became aware', value: record.aware_at ? fmtDateTime(record.aware_at) : null },
            { label: 'Deadline', value: <DeadlineCell inc={record} /> },
            { label: 'Reported by', value: record.reported_by || null },
          ]}
          footer={editing ? (
            <>
              <button type="button" onClick={() => setModal(null)} className="btn-secondary">Cancel</button>
              <button type="button" onClick={submit} disabled={saving} className="btn-primary">{saving ? 'Saving…' : 'Save changes'}</button>
            </>
          ) : (
            <>
              <button type="button" onClick={() => del(record)} className="btn-ghost text-red-700 hover:text-red-800 mr-auto"><Trash2 size={14} /> Delete</button>
              <button type="button" onClick={() => openEdit(record)} className="btn-secondary"><Pencil size={14} /> Edit</button>
              {!record.notified_at && <button type="button" onClick={() => notify(record)} className="btn-primary"><CheckCircle2 size={15} /> Record as reported</button>}
            </>
          )}
        >
          {editing ? form : (
            <div className="space-y-4">
              <p className="text-[12.5px] text-slate-500 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
                The report itself is made through the My Aged Care provider portal. Recording it here stamps the date and time, so the register shows whether the deadline was met. Nothing is sent to the Commission from this screen.
              </p>
              <SirsTriage key={record.id + (record.triaged_at || '')} inc={record} onDone={load} />
              {!record.reportable && <p className="text-[12.5px] text-slate-500">Marked as not reportable under SIRS.</p>}
              {record.notified_at ? (
                <div className="rounded-lg border border-slate-200 p-3 space-y-2 text-[12.5px]">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium text-slate-800 flex items-center gap-1.5"><CheckCircle2 size={14} className="text-emerald-600" /> Recorded as reported</p>
                      <p className="text-slate-600 mt-0.5 tabular-nums">{fmtDateTime(record.notified_at)}{record.notified_by ? `, by ${record.notified_by}` : ''}</p>
                      <p className="text-slate-600">Portal reference {record.portal_reference ? <span className="font-medium">{record.portal_reference}</span> : <span className="text-slate-500">not recorded</span>}</p>
                    </div>
                    {correcting?.id !== record.id && (
                      <span className="flex flex-wrap gap-1">
                        <button type="button" onClick={() => startCorrect(record)} className="btn-ghost btn-sm"><Pencil size={13} /> Correct</button>
                        <button type="button" onClick={() => undoReported(record)} className="btn-ghost btn-sm text-red-700 hover:text-red-800"><RotateCcw size={13} /> Undo reported</button>
                      </span>
                    )}
                  </div>
                  {correcting?.id === record.id && (
                    <div className="flex flex-wrap items-end gap-2 pt-2 border-t border-slate-100">
                      <Field label="Reported on">
                        {(id) => <input id={id} type="datetime-local" className="input-field !w-auto" value={correcting.notifiedAt} max={sydneyInputValue()} onChange={(e) => { const v = e.target.value; setCorrecting((p) => ({ ...p, notifiedAt: v })); }} />}
                      </Field>
                      <Field label="Portal reference" className="flex-1 min-w-[10rem]">
                        {(id) => <input id={id} className="input-field" value={correcting.reference} onChange={(e) => { const v = e.target.value; setCorrecting((p) => ({ ...p, reference: v })); }} placeholder="From the portal" />}
                      </Field>
                      <button type="button" onClick={() => setCorrecting(null)} className="btn-ghost btn-sm">Cancel</button>
                      <button type="button" onClick={() => saveCorrection(record)} disabled={savingCorrection} className="btn-primary btn-sm">{savingCorrection ? 'Saving…' : 'Save'}</button>
                    </div>
                  )}
                </div>
              ) : record.portal_reference ? (
                <p className="text-[12.5px] text-slate-600">Portal reference <span className="font-medium">{record.portal_reference}</span></p>
              ) : null}
              {record.reportable && !record.notified_at && (pack?.id !== record.id ? (
                <button type="button" onClick={() => preparePack(record)} className="btn-secondary btn-sm">Prepare the portal report</button>
              ) : (
                <div className="rounded-lg border border-slate-200 p-3 space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[13px] font-medium text-slate-800">Report it in the My Aged Care provider portal</p>
                    <button type="button" onClick={() => copy(pack.text, 'Report')} className="btn-secondary btn-sm">Copy all</button>
                  </div>
                  <ol className="list-decimal pl-5 space-y-0.5 text-[12.5px] text-slate-600">
                    {pack.steps.map((s) => <li key={s}>{s}</li>)}
                  </ol>
                  {pack.missing?.length > 0 && (
                    <p className="text-[12px] text-amber-800">Not recorded yet: {pack.missing.join(', ')}. Add them with Edit if you have them.</p>
                  )}
                  {pack.sections.map((sec) => (
                    <div key={sec.heading}>
                      <p className="text-[11.5px] font-semibold text-slate-500 mb-1">{sec.heading}</p>
                      <dl className="space-y-1">
                        {sec.fields.map(([k, v]) => (
                          <div key={k} className="flex items-start justify-between gap-3 text-[12.5px]">
                            <dt className="text-slate-500 shrink-0 w-40">{k}</dt>
                            <dd className="flex-1 text-slate-800 whitespace-pre-wrap break-words">{v || <span className="text-slate-300">not recorded</span>}</dd>
                            {v ? <button type="button" onClick={() => copy(String(v), k)} className="btn-ghost btn-sm !px-1.5 shrink-0">Copy</button> : <span className="w-10" />}
                          </div>
                        ))}
                      </dl>
                    </div>
                  ))}
                  <div className="flex items-end gap-2 pt-1 border-t border-slate-100">
                    <Field label="Portal notification reference" className="flex-1">
                      {(id) => <input id={id} className="input-field" value={portalRef} onChange={(e) => setPortalRef(e.target.value)} placeholder="From the portal once submitted" />}
                    </Field>
                    <button type="button" onClick={() => notify(record, portalRef.trim())} disabled={!portalRef.trim()} className="btn-primary">Record as reported</button>
                  </div>
                </div>
              ))}
              <dl className="space-y-4">
                <TextBlock label="Description" text={record.description} />
                <TextBlock label="Immediate actions taken" text={record.immediate_actions} />
                <TextBlock label="Follow-up" text={record.follow_up} />
              </dl>
              <CiActions source="sirs" sourceId={record.id} title="Investigation and follow-up" />
              <ToImprovement source="sirs" sourceId={record.id} suggested={`After incident: ${record.category}`} />
            </div>
          )}
        </DetailDrawer>
      )}

      <Dialog
        open={!!modal && !editing}
        onClose={closeModal}
        size="md"
        title="Log an incident"
        description="Saving sets the reporting deadline from when you became aware of it, or from now if you leave that blank. Report it through the My Aged Care provider portal, then record that here."
        footer={(
          <>
            <button type="button" onClick={closeModal} className="btn-secondary">Cancel</button>
            <button type="button" onClick={submit} disabled={saving} className="btn-primary">{saving ? 'Saving…' : 'Log incident'}</button>
          </>
        )}
      >
        {form}
      </Dialog>
    </div>
  );
}
